import { useGSAP } from '@gsap/react';
import { graphql } from 'gatsby';
import gsap from 'gsap';
import React, { useRef } from 'react';
import { FaLinkedin } from 'react-icons/fa';
import { ModuleBgColor } from '../../../templates/Page';
import { getUrlFromVersatileLink } from '../../../utils/sanity';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import SmartLink from '../SmartLink';
import * as styles from './OrganizationalChartModule.module.scss';
import { Person } from './TeamModule';

export const OrganizationalChartModuleFragment = graphql`
  fragment OrganizationalChartModule on SanityPageOrganizationalChartModule {
    title {
      ...LocaleStringFragment
    }
    socialEntitiesBlocks {
      title {
        ...LocaleStringFragment
      }
      socialEntities {
        person {
          ...Person
        }
        position {
          ...LocaleStringFragment
        }
      }
      showPersonImages
    }
  }
`;

export interface OrganizationalChartModuleProps {
  title: string;
  socialEntitiesBlocks: Array<{
    title: string;
    socialEntities: Array<{
      person: Person;
      position: string;
    }>;
    showPersonImages?: boolean;
  }>;
  className?: string;
}

export function getModuleBgColor(props: OrganizationalChartModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'white';
}

function OrganizationalChartModule(
  props: OrganizationalChartModuleProps & CommonModuleProps,
): React.ReactElement {
  const { title, socialEntitiesBlocks, className, previousModuleBgColor, moduleId } = props;

  const containerRef = useRef<HTMLElement | null>(null);

  useGSAP(
    () => {
      const socialEntitiesBlocksEls = gsap.utils.toArray<HTMLDivElement>(
        `.${styles.blockContainer}`,
      );

      socialEntitiesBlocksEls.forEach(block => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: block,
            start: 'top 95%',
          } satisfies ScrollTrigger.Vars,
        });
        gsap.context(() => {
          // animate each element
          tl.from(`.${styles.personContainer} > *`, {
            opacity: 0,
            duration: 0.6,
            scale: 0.8,
            y: 60,

            stagger: 0.2,
          });
        }, block);
      });
    },
    { scope: containerRef },
  );

  return (
    <ModuleLayout
      id={moduleId}
      className={clsx(className, styles.container)}
      title={title}
      moduleRef={containerRef}
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
    >
      <div className={styles.contentContainer}>
        {socialEntitiesBlocks.map((block, blockIndex) => (
          <div className={styles.blockContainer} key={blockIndex}>
            <h3 className={styles.blockTitle}>{block.title}</h3>
            <div
              className={clsx(
                styles.entitiesContainer,
                block.showPersonImages && styles.withPersonImage,
              )}
            >
              {(block.showPersonImages
                ? block.socialEntities.filter(socialEntity => socialEntity.person.image)
                : block.socialEntities
              ).map((socialEntity, personIndex) => (
                <div
                  className={clsx(
                    styles.personContainer,
                    personIndex % 2 !== 0 && styles.oddContainer,
                  )}
                  key={personIndex}
                >
                  <div className={styles.personWrapper}>
                    {((block.showPersonImages && socialEntity.person.image) ||
                      socialEntity.person.linkedinUrl) && (
                      <div className={styles.imageContainer}>
                        {block.showPersonImages && socialEntity.person.image && (
                          <div className={clsx(styles.imageWrapper)}>
                            <Image image={socialEntity.person.image} />
                          </div>
                        )}
                        {socialEntity.person.linkedinUrl && (
                          <SmartLink
                            to={getUrlFromVersatileLink({
                              linkType: 'externalLink',
                              externalLink: {
                                url: socialEntity.person.linkedinUrl,
                              },
                            })}
                            className={clsx(styles.linkedinContainer)}
                          >
                            <div className={styles.visuallyHidden}>
                              {socialEntity.person.name} Linkedin
                            </div>
                            <FaLinkedin className={styles.linkedinIcon} />
                          </SmartLink>
                        )}
                      </div>
                    )}
                    <div className={styles.textContainer}>
                      <h4 className={styles.name}>{socialEntity.person.name}</h4>
                      <p className={styles.position}>{socialEntity.position}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </ModuleLayout>
  );
}

export default OrganizationalChartModule;
